import React, { createRef, useEffect } from 'react';
import ReactMapGL, { Layer, Marker, NavigationControl, Source, ViewStateChangeEvent, MapRef } from 'react-map-gl';

import 'mapbox-gl/dist/mapbox-gl.css';
// mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;
import './DefaultMapbox.scss';
import { decodePolyline } from './Utils';
import useRefDimensions from '../../hooks/useRefDimensions';

import mapboxgl from 'mapbox-gl';

interface MarkerProps {
  longitude: number;
  latitude: number;
  offsetX?: number;
  offsetY?: number;
  className?: string;
  style?: React.CSSProperties;
  content: React.ReactNode;
}

interface PolylineProps {
  polyline: string;
  strokeColor: string;
  strokeWeight: number;
  strokeOpacity: number;
}

interface DefaultMapboxProps {
  markers?: MarkerProps[];
  lngLatMove?: [number, number];
  polyline?: string;
  polylines?: PolylineProps[];
  className?: string;
  isControl?: boolean;
  [key: string]: any;
}

const DefaultMapbox: React.FC<DefaultMapboxProps> = ({
  markers,
  lngLatMove,
  polyline,
  polylines,
  className = '',
  isControl,
  ...props
}) => {
  const mapRef = React.useRef<MapRef>(null);
  const divRef = createRef<HTMLDivElement>();
  const dimensions = useRefDimensions(divRef);

  const navStyle = {
    top: 0,
    left: 0,
    padding: '10px',
  };

  const [viewState, setViewState] = React.useState({
    longitude: 4.931713,
    latitude: 52.354399,
    zoom: 12,
  });

  useEffect(() => {
    if (lngLatMove?.length === 2 && mapRef.current) {
      mapRef.current.flyTo({ center: lngLatMove, zoom: 8 });
    }
  }, [lngLatMove, mapRef]);

  useEffect(() => {
    if (markers?.length > 0 && dimensions.width > 10 && dimensions.height > 10 && mapRef.current) {
      if (markers.length === 1) {
        // For single marker, center on it with a reasonable zoom
        mapRef.current.flyTo({
          center: [markers[0].longitude, markers[0].latitude],
          zoom: 14,
          duration: 1000,
        });
      } else {
        // For multiple markers, fit bounds
        const bounds = new mapboxgl.LngLatBounds();
        markers.forEach((marker) => {
          bounds.extend([marker.longitude, marker.latitude]);
        });

        mapRef.current.fitBounds(bounds, {
          padding: 50,
          duration: 1000,
        });
      }
    }
  }, [markers, dimensions.width, dimensions.height]);

  const showMarkers = React.useMemo(
    () =>
      markers?.map((marker, index) => {
        return (
          <Marker
            key={index + 'marker'}
            longitude={marker.longitude}
            latitude={marker.latitude}
            offset={[marker.offsetX, marker.offsetY]}
            className={marker.className}
            style={marker.style}
          >
            {marker.content}
          </Marker>
        );
      }),
    [markers],
  );

  // Process polylines from either single polyline or array of polylines
  const processedPolylines = React.useMemo(() => {
    if (polylines && polylines.length > 0) {
      return polylines;
    } else if (polyline) {
      return [
        {
          polyline,
          strokeColor: '#3388ff',
          strokeWeight: 3,
          strokeOpacity: 0.8,
        },
      ];
    }
    return [];
  }, [polyline, polylines]);

  return (
    <div
      ref={divRef}
      style={{ width: '100%', height: '100%', position: 'relative' }}
      className={`DefaultMapbox ${className}`}
    >
      <ReactMapGL
        ref={mapRef}
        {...viewState}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/maurice-sparqle/cll2ad40r00e801pd7cvn31eo"
        mapboxAccessToken={process.env.REACT_APP_MAP_BOX_KEY}
        onMove={(evt: ViewStateChangeEvent) => setViewState(evt.viewState)}
      >
        {showMarkers}
        {processedPolylines.length > 0 && (
          <Source
            type="geojson"
            data={{
              type: 'FeatureCollection',
              features: [
                ...processedPolylines.map((po) => {
                  const polyline = po.polyline;
                  return {
                    type: 'Feature' as const,
                    properties: {
                      stroke: po.strokeColor,
                      strokeWeight: po.strokeWeight,
                      strokeOpacity: po.strokeOpacity,
                    },
                    geometry: {
                      type: 'LineString' as const,
                      coordinates:
                        polyline && decodePolyline(polyline, 5).length > 0
                          ? decodePolyline(polyline, 5).map((c) => [c.lng, c.lat])
                          : [],
                    },
                  };
                }),
              ],
            }}
          >
            <Layer
              id="lineLayer"
              type="line"
              layout={{
                'line-join': 'round',
                'line-cap': 'round',
              }}
              paint={{
                'line-color': ['get', 'stroke'],
                'line-width': ['get', 'strokeWeight'],
                'line-opacity': ['get', 'strokeOpacity'],
              }}
            />
          </Source>
        )}
        {props.children}
        {isControl && <NavigationControl style={navStyle} />}
      </ReactMapGL>
    </div>
  );
};

export default DefaultMapbox;
